import { PropsWithChildren, MouseEventHandler } from 'react';

import cn from 'classnames';

import { t } from '../../../typography';
import { type IconWeight } from '../../icons';
import { availableIcons } from '../ButtonIcon';

import { type ButtonVariants } from './Button.css';
import * as css from './Button.css';

export type ButtonProps = {
    /** Native HTML button type. @default 'button' */
    type?: 'submit' | 'button';

    /** Visual style variant defined in Button.css. */
    variant?: ButtonVariants['variant'];

    /** Controls button size (spacing and typography). @default 'medium' */
    size?: ButtonVariants['size'];

    /** Click handler (not fired when disabled or loading). */
    onClick?: MouseEventHandler<HTMLButtonElement>;

    /** Icon name from availableIcons to render inside the button. */
    icon?: keyof typeof availableIcons;

    /** Disables the button and prevents interaction. @default false */
    isDisabled?: boolean;

    /** Applies rounded styling. @default false */
    rounded?: boolean;

    /** Visual weight/style of the icon. */
    iconWeight?: IconWeight;

    /** Position of the icon relative to text. @default 'before' */
    iconSide?: 'before' | 'after';

    /** Overrides the icon width and height in pixels. */
    iconSize?: number;

    /** Icon color value. @default 'currentColor' */
    iconColor?: string;

    /** Shows loading spinner and disables the button. @default false */
    isLoading?: boolean;

    /** Associates the button with a form by id. */
    form?: string;
};

const fontSizes: Record<NonNullable<ButtonVariants['size']>, string> = {
    icon: t({ variant: 'h4', color: 'inherit' }),
    action: t({ variant: 'h4', color: 'inherit' }),
    tiny: t({ variant: 'h4', color: 'inherit' }),
    small: t({ variant: 'h4', color: 'inherit' }),
    medium: t({ variant: 'h3', color: 'inherit' }),
    large: t({ variant: 'h2', color: 'inherit' }),
};

export function Button({
    children,
    type = 'button',
    variant,
    size = 'medium',
    isDisabled = false,
    rounded = false,
    icon,
    iconWeight,
    iconSide = 'before',
    iconSize,
    iconColor = 'currentColor',
    onClick,
    isLoading = false,
    form,
}: PropsWithChildren<ButtonProps>) {
    const Icon = icon && availableIcons[icon].component;

    // Force `primary` variant into `secondary` when it's disabled.
    const effectiveVariant = variant === 'primary' && isDisabled ? 'secondary' : variant;

    return (
        <button
            className={css.button({ variant: effectiveVariant, size, isDisabled, isLoading, rounded })}
            onClick={!isLoading ? onClick : undefined}
            disabled={isDisabled || isLoading}
            type={type}
            form={form}
        >
            <span className={css.content} style={{ opacity: isLoading ? 0 : 1 }}>
                {children && <span className={cn([css.text, fontSizes[size]])}>{children}</span>}
                {Icon && (
                    <span className={css.icon({ side: iconSide })}>
                        <Icon
                            weight={iconWeight}
                            color={iconColor}
                            width={iconSize ?? availableIcons[icon].defaultSize}
                            height={iconSize ?? availableIcons[icon].defaultSize}
                        />
                    </span>
                )}
            </span>
            {isLoading && (
                <svg
                    className={css.spinner}
                    width={iconSize ?? 24}
                    height={iconSize ?? 24}
                    viewBox="0 0 66 66"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <circle
                        className={css.path}
                        fill="none"
                        strokeWidth="6"
                        strokeLinecap="round"
                        cx="33"
                        cy="33"
                        r="30"
                    />
                </svg>
            )}
        </button>
    );
}
